import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  EndorsementServiceFeaturesGetAllCertificatesGetAllCertificatesDto as CertificatesDto,
  EndorsementServiceFeaturesGetApplicationGroupByUidApplicationGroupDto as ApplicationGroupDto,
  // eslint-disable-next-line max-len
  EndorsementServiceFeaturesGetCertificateAuthoritiesAvailableCertificateAuthoritiesDto as AuthoritiesDto,
  EndorsementServiceFeaturesGetAvailableNationalitiesAvailableNationalitiesDto as NationalitiesDto,
} from '@sdir/httpclient/lib/clients/aps/endorsement';
import { formatDateString } from '@sdir/blueprint.aps/lib/helpers/formattingHelpers';
import { DataTable } from '@sdir/sds';
import { CertificatesSummaryHeaders } from '../../helpers/TableHeaders';

interface ApplicationSummaryProps {
  application?: ApplicationGroupDto;
  authorities?: AuthoritiesDto[] | null;
  certificates?: CertificatesDto[] | null;
  nationalities?: NationalitiesDto[] | null;
}

const ApplicationSummary: React.FC<ApplicationSummaryProps> = ({
  application,
  authorities,
  certificates,
  nationalities,
}) => {
  const intl = useIntl();

  const nationality = useMemo(() => {
    const nationText = nationalities?.find((n) => application?.person?.nationality === n.idIso2);
    return nationText?.[intl?.locale] || application?.person?.nationality;
  }, [intl?.locale, application?.person, nationalities]);

  const certificatesData = useMemo(() => {
    const certItems = application?.certificates || [];
    return certItems.map((cert) => {
      const certUid = cert?.uid;
      const existingCertUid = cert?.existingCertificateUid;
      let certName = certUid;
      let existingCertName = existingCertUid;
      const authority = authorities?.find((a) => a.uid === cert?.certificateAuthorityUid);
      const issuer = authority?.[intl?.locale] || cert?.certificateAuthorityUid;
      certificates?.forEach((g) => {
        g?.certificates?.forEach((c) => {
          if (c.uid === certUid) {
            certName = c?.certificateName;
          }
          if (c.uid === existingCertUid) {
            existingCertName = c?.certificateName;
          }
        });
      });
      return {
        endorsement: certName || '',
        nationalCertificate: existingCertName || '',
        issuedBy: issuer || '',
        certificateNumber: cert?.certificateNumber || '',
        expiry: formatDateString(cert?.expirationDate || ''),
      };
    });
  }, [application?.certificates, authorities, certificates, intl?.locale]);

  if (!application) return <></>;

  return (
    <Wrapper>
      <SeafarerInfo>
        <Item>
          <Label>{intl.formatMessage({ id: 'application.sailor.name' })}</Label>
          <div>{application?.person?.name || ''}</div>
        </Item>
        <Item>
          <Label>{intl.formatMessage({ id: 'general.form.dateOfBirth' })}</Label>
          <div>
            {application?.person?.dateOfBirth
              ? formatDateString(application.person.dateOfBirth)
              : ''}
          </div>
        </Item>
        <Item>
          <Label>{intl.formatMessage({ id: 'application.sailor.citizenship' })}</Label>
          <div>{nationality || ''}</div>
        </Item>
        <Item>
          <Label>{intl.formatMessage({ id: 'general.form.email' })}</Label>
          <div>{application?.person?.email || ''}</div>
        </Item>
      </SeafarerInfo>
      <StyledDataTable columns={CertificatesSummaryHeaders} data={certificatesData} />
    </Wrapper>
  );
};

export default ApplicationSummary;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SeafarerInfo = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 10px;
`;

const Item = styled.div`
  margin-right: 30px;
  width: 25%;
`;

const Label = styled.div`
  font-weight: 600;
  margin-bottom: 5px;
`;

const StyledDataTable = styled(DataTable)`
  margin-left: -5px;
  background-color: transparent;
  thead tr th {
    padding-left: 0;
    button {
      text-align: left;
    }
  }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.primary8};
    min-height: 40px;

    &:last-child {
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary8};
    }
  }
`;
